import { apiFetch } from './auth-service';

export interface PartnerDoc {
  _id: string;
  institutionName: string;
  type: 'University' | 'Accelerator' | 'Government' | 'Corporate';
  location: string;
  region: string;
  studentCount: number | string;
  primaryContact: string;
  contactEmail: string;
  website: string;
  joinedAt: string;
}

export interface MentorRow {
  id: string;
  name: string;
  avatar: string;
  title: string;
  company: string;
  region: string;
  experience: string;
  experienceYears: number;
  expertise: string[];
  industry: string[];
  specialties: string[];
  menteesCount: number;
  sessionsLed: number;
  status: 'pending' | 'approved' | 'rejected' | 'unknown';
  bio: string;
}

export interface MentorMentee {
  founderId: string;
  founderName: string;
  startupId?: string;
  startupName?: string;
}

export interface MentorSession {
  id: string;
  title: string;
  scheduledAt: string;
  attendeeCount: number;
  status: string;
}

export interface MentorDetail extends MentorRow {
  phone: string;
  linkedIn: string;
  twitter: string;
  website: string;
  mentees: MentorMentee[];
  recentSessions: MentorSession[];
}

export interface InvestorRow {
  id: string;
  name: string;
  avatar: string;
  title: string;
  company: string;
  region: string;
  experience: string;
  industry: string[];
  specialties: string[];
  pitchesReviewed: number;
  bio: string;
}

export const getMentorRows = async (): Promise<MentorRow[]> => {
  const r = await apiFetch.get<{ users: any[] }>('/users?userType=mentor&limit=200');
  return (r.users || []).map(u => ({
    id: u._id,
    name: u.name,
    avatar: u.avatar || '',
    title: u.title || '',
    company: u.company || '',
    region: u.region || '',
    experience: u.experience || '',
    experienceYears: 0,
    expertise: u.expertise || u.mentor?.expertise || [],
    industry: u.industry || [],
    specialties: u.specialties || u.mentor?.specialties || [],
    menteesCount: 0,
    sessionsLed: 0,
    status: (u.status as MentorRow['status']) || 'unknown',
    bio: u.bio || '',
  }));
};

export const getMentorDetail = async (id: string): Promise<MentorDetail | null> => {
  const r = await apiFetch.get<{ user: any }>(`/users/${id}`);
  const u = r.user;
  if (!u) return null;
  return {
    id: u._id,
    name: u.name,
    avatar: u.avatar || '',
    title: u.title || '',
    company: u.company || '',
    region: u.region || '',
    experience: u.experience || '',
    experienceYears: 0,
    expertise: u.expertise || u.mentor?.expertise || [],
    industry: u.industry || [],
    specialties: u.specialties || u.mentor?.specialties || [],
    menteesCount: 0,
    sessionsLed: 0,
    status: (u.status as MentorRow['status']) || 'unknown',
    bio: u.bio || '',
    phone: u.phone || '',
    linkedIn: u.social?.linkedIn || '',
    twitter: u.social?.twitter || '',
    website: u.social?.website || '',
    mentees: [],
    recentSessions: [],
  };
};

export const getPartnerRows = async (): Promise<PartnerDoc[]> => {
  const r = await apiFetch.get<{ users: any[] }>('/users?userType=partner&limit=200');
  return (r.users || []).map(u => ({
    _id: u._id,
    institutionName: u.partner?.institution || u.name,
    type: 'University' as const,
    location: u.region || '',
    region: u.region || '',
    studentCount: u.partner?.studentCount || '',
    primaryContact: u.name,
    contactEmail: u.email,
    website: u.social?.website || '',
    joinedAt: u.createdAt || '',
  }));
};

export const getInvestorRows = async (): Promise<InvestorRow[]> => {
  const r = await apiFetch.get<{ users: any[] }>('/users?userType=investor&limit=200');
  return (r.users || []).map(u => ({
    id: u._id,
    name: u.name,
    avatar: u.avatar || '',
    title: u.title || '',
    company: u.company || u.investor?.fundName || '',
    region: u.region || '',
    experience: u.experience || '',
    industry: u.industry || [],
    specialties: u.specialties || u.investor?.specialties || [],
    pitchesReviewed: 0,
    bio: u.bio || '',
  }));
};

export const getAssociateKpis = async (): Promise<{
  founders: number;
  mentors: number;
  partners: number;
  investors: number;
}> => {
  const [foundersRes, mentorsRes, partnersRes, investorsRes] = await Promise.allSettled([
    apiFetch.get<{ startups: any[] }>('/startups'),
    apiFetch.get<{ total: number }>('/users?userType=mentor&limit=1'),
    apiFetch.get<{ total: number }>('/users?userType=partner&limit=1'),
    apiFetch.get<{ total: number }>('/users?userType=investor&limit=1'),
  ]);
  return {
    founders: foundersRes.status === 'fulfilled' ? (foundersRes.value.startups?.length || 0) : 0,
    mentors:  mentorsRes.status  === 'fulfilled' ? (mentorsRes.value.total  || 0) : 0,
    partners: partnersRes.status === 'fulfilled' ? (partnersRes.value.total || 0) : 0,
    investors: investorsRes.status === 'fulfilled' ? (investorsRes.value.total || 0) : 0,
  };
};
